/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { UnpackNestedValue } from "react-hook-form";
import { When } from "react-if";
import { Box, theme, Text } from "@nordcloud/gnui";
import {
  WorkflowExternalIntegrationType,
  WorkflowTriggerType,
} from "~/generated/graphql";
import { isEmpty } from "~/tools";
import { WorkflowDataType } from "../constants";
import { WorkflowFormData, WorkflowFormMode } from "../types";
import { SelectDataHolder } from "./components/SelectDataHolder";
import { SendDataHolder } from "./components/SendDataHolder";
import { TriggerHolder } from "./components/TriggerHolder";

type Props = {
  formMode: WorkflowFormMode;
  title?: string;
  triggers: TriggerConfig[];
  selectDataComponent?: SelectDataComponentConfig;
  sendDataComponents: SendDataComponentConfig[];
};

export function WorkflowCanvas({
  formMode,
  title,
  triggers,
  selectDataComponent,
  sendDataComponents,
}: Props) {
  return (
    <div css={{ width: "100%" }}>
      <When condition={title}>
        <Text weight="bold">{title}</Text>
      </When>
      <Box border="disabled" backgroundColor={theme.color.background.ui04}>
        <TriggerComponents formMode={formMode} components={triggers} />
        <SelectDataComponent
          formMode={formMode}
          component={selectDataComponent}
        />
        <SendDataComponents
          formMode={formMode}
          components={sendDataComponents}
        />
      </Box>
    </div>
  );
}

type TriggerComponentsProps = {
  formMode: WorkflowFormMode;
  components: TriggerConfig[];
};

function TriggerComponents({ components, formMode }: TriggerComponentsProps) {
  if (isEmpty(components)) {
    return <TriggerHolder isEmpty formMode={formMode} />;
  }

  return (
    <>
      {components.map(({ type, defaultSchedulerValues, onSettingsClick }) => (
        <TriggerHolder
          key={type}
          type={type}
          formMode={formMode}
          initialValues={defaultSchedulerValues}
          onSettingsClick={onSettingsClick}
        />
      ))}
    </>
  );
}

type SelectDataComponentProps = {
  formMode: WorkflowFormMode;
  component?: SelectDataComponentConfig;
};

function SelectDataComponent({
  component,
  formMode,
}: SelectDataComponentProps) {
  if (component === undefined) {
    return <SelectDataHolder isEmpty formMode={formMode} />;
  }

  return <SelectDataHolder formMode={formMode} {...component} />;
}

type SendDataComponentsProps = {
  formMode: WorkflowFormMode;
  components: SendDataComponentConfig[];
};

function SendDataComponents({ components, formMode }: SendDataComponentsProps) {
  if (isEmpty(components)) {
    return <SendDataHolder isEmpty formMode={formMode} />;
  }

  return (
    <>
      {components.map(
        ({
          type,
          onSettingsClick,
          defaultEmailValues,
          defaultServiceNowValues,
          defaultSlackValues,
        }) => (
          <SendDataHolder
            key={type}
            type={type}
            formMode={formMode}
            initialValues={
              defaultServiceNowValues ??
              defaultEmailValues ??
              defaultSlackValues
            }
            onSettingsClick={onSettingsClick}
          />
        )
      )}
    </>
  );
}

export type TriggerConfig = {
  type: WorkflowTriggerType;
  onSettingsClick?: () => void;
  defaultSchedulerValues?: UnpackNestedValue<WorkflowFormData>["scheduler"];
};

type SelectDataComponentConfig = {
  type: WorkflowDataType;
  onSettingsClick?: () => void;
  defaultSavingsSuggestionsValues?: UnpackNestedValue<WorkflowFormData>["savingsSuggestions"];
};

export type SendDataComponentConfig = {
  type: WorkflowExternalIntegrationType;
  onSettingsClick?: () => void;
  defaultServiceNowValues?: UnpackNestedValue<WorkflowFormData>["serviceNow"];
  defaultEmailValues?: UnpackNestedValue<WorkflowFormData>["email"];
  defaultSlackValues?: UnpackNestedValue<WorkflowFormData>["slack"];
};
